import { FieldItem, ValidateRule } from '@/coreDomainsModule/constant/validateRules';
import { FormValidateFunctionFactory } from '@/coreDomainsModule/factories/formValidateFunctionFactory';
import { FormErrorMessageFactory } from '@/coreDomainsModule/factories/formErrorMessageFactory';
import { computed, ref, Ref } from 'vue';

/**
 * useForm State Type
 */
export type FieldState = Record<
  string,
  {
    isError: Ref<boolean>;
    errorMessage: Ref<string>;
    validate: ReturnType<typeof FormValidateFunctionFactory.create>;
    value: Ref<string>;
    setValue: (value: string, shouldValidate?: boolean) => void;
    rules: readonly ValidateRule[];
  }
>;

/**
 *　　フォームの値を管理する
 * @param fieldItems - フィールドのアイテム
 */
export const useForms = (fieldItems: FieldItem[]) => {
  const state: FieldState = {};

  const fieldItemMap: Record<string, FieldItem> = {};

  /**
   * フィールド定義のマップを更新する
   * @param targetFieldItems - フィールド定義
   */
  const syncFieldItemMap = (targetFieldItems: FieldItem[]): void => {
    for (const key in fieldItemMap) {
      delete fieldItemMap[key];
    }
    targetFieldItems.forEach((fieldItem) => {
      fieldItemMap[fieldItem.name] = fieldItem;
    });
  };

  /**
   * カスタムバリデーションを実行する
   * @param fieldName - フィールド名
   */
  const runCustomValidateFunctions = (fieldName: string): string => {
    const targetFieldItem = fieldItemMap[fieldName];
    if (targetFieldItem?.customValidateFunctions == null) {
      return '';
    }

    for (const validateFunction of targetFieldItem.customValidateFunctions) {
      const errorMessage = validateFunction();
      if (errorMessage != null && errorMessage !== '') {
        return errorMessage;
      }
    }

    return '';
  };

  /**
   * 単一フィールドのバリデーションを実行する
   * @param fieldName - フィールド名
   */
  const validateSingleField = (fieldName: string): boolean => {
    const targetState = state[fieldName];
    const { isValid, invalidRules } = targetState.validate(targetState.value.value);

    if (!isValid) {
      targetState.isError.value = true;
      targetState.errorMessage.value = FormErrorMessageFactory.create(invalidRules);
      return false;
    }

    const customErrorMessage = runCustomValidateFunctions(fieldName);
    if (customErrorMessage !== '') {
      targetState.isError.value = true;
      targetState.errorMessage.value = customErrorMessage;
      return false;
    }

    targetState.isError.value = false;
    targetState.errorMessage.value = '';
    return true;
  };

  /**
   * フィールド状態を作成する
   * @param fieldItem - フィールド定義
   */
  const createFieldState = (fieldItem: FieldItem) => {
    return {
      value: ref(''),
      isError: ref(false),
      errorMessage: ref(''),
      validate: FormValidateFunctionFactory.create(fieldItem.rules),
      /**
       * フィールドの値を設定
       * @param value - 設定する値
       * @param shouldValidate - バリデーションを実行するかどうか
       */
      setValue: (value: string, shouldValidate = true) => {
        state[fieldItem.name].value.value = value;
        if (!shouldValidate) {
          return;
        }
        validateSingleField(fieldItem.name);
      },
      rules: fieldItem.rules,
    };
  };

  syncFieldItemMap(fieldItems);

  for (const fieldItem of fieldItems) {
    state[fieldItem.name] = createFieldState(fieldItem);
  }

  /**
   * 全てのバリデーションを実行
   */
  const allValidate = (): boolean => {
    let result = true;
    for (const key in state) {
      const isValid = validateSingleField(key);
      if (!isValid) {
        result = false;
      }
    }
    return result;
  };

  /**
   * フォームが全てOKかどうか
   */
  const isAllValid = computed(() => {
    return Object.values(state).every((field) => !field.isError.value);
  });

  /**
   * フォームのリセット（初期化）
   * @param fieldItems - フィールドのアイテム
   */
  const resetForm = (fieldItems: FieldItem[] = []): void => {
    syncFieldItemMap(fieldItems);

    // 対応するフィールドの初期化
    // 対応するフィールドがない場合は新規に追加
    for (const fieldItem of fieldItems) {
      if (state[fieldItem.name]) {
        state[fieldItem.name].value.value = '';
        state[fieldItem.name].isError.value = false;
        state[fieldItem.name].errorMessage.value = '';
        state[fieldItem.name].validate = FormValidateFunctionFactory.create(fieldItem.rules);
        state[fieldItem.name].rules = fieldItem.rules;
      } else {
        state[fieldItem.name] = createFieldState(fieldItem);
      }
    }
    // 対応するフィールドがない場合は削除
    for (const key in state) {
      if (!fieldItems.some((fieldItem) => fieldItem.name === key)) {
        delete state[key];
      }
    }
  };

  return {
    fieldState: state,
    allValidate,
    resetForm,
    isAllValid,
  };
};
